import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Search, Plus } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

const SWATCHES = ['#2F6B57', '#C86B4A', '#2E6F7E', '#6B3F69', '#3A5A40', '#B7791F'];

// Dialog to add selected contacts into an existing list OR save as a new list.
export const AddToListDialog = ({ open, onOpenChange, contactIds, onDone }) => {
  const [lists, setLists] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2F6B57');

  useEffect(() => {
    if (open) api.get('/groups').then((r) => setLists(r.data)).catch(() => {});
  }, [open]);

  const close = (v) => { if (!v) { setName(''); setColor('#2F6B57'); } onOpenChange(v); };

  const addToExisting = async (listId) => {
    setSaving(true);
    try {
      const res = await api.post(`/groups/${listId}/add`, { ids: contactIds });
      toast.success(`Added to list (${res.data.count} total)`);
      onDone && onDone();
      close(false);
    } catch (e) { toast.error('Could not add to list'); }
    finally { setSaving(false); }
  };

  const createNew = async () => {
    if (!name.trim()) { toast.error('Name your list'); return; }
    setSaving(true);
    try {
      await api.post('/groups', { name: name.trim(), color, contact_ids: contactIds });
      toast.success(`Saved "${name.trim()}" with ${contactIds.length} contacts`);
      onDone && onDone();
      close(false);
    } catch (e) { toast.error('Could not create list'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="add-to-list-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add {contactIds.length} to a list</DialogTitle></DialogHeader>
        <Tabs defaultValue="existing" className="mt-2">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="existing" data-testid="addlist-tab-existing">Existing list</TabsTrigger>
            <TabsTrigger value="new" data-testid="addlist-tab-new">Save as new list</TabsTrigger>
          </TabsList>
          <TabsContent value="existing" className="mt-4">
            {lists.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No lists yet. Create one in the next tab.</p> : (
              <div className="space-y-2 max-h-64 overflow-auto">
                {lists.map((l) => (
                  <button key={l.id} onClick={() => addToExisting(l.id)} disabled={saving}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-secondary transition-colors text-left" data-testid="addlist-existing-item">
                    <span className="h-3 w-3 rounded-full" style={{ background: l.color }} />
                    <span className="text-sm font-medium flex-1">{l.name}</span>
                    <span className="text-xs text-muted-foreground">{(l.contact_ids || []).length} people</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="new" className="mt-4 space-y-4">
            <div className="space-y-1.5"><Label>List name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Close Friends, Family, VIPs" className="h-11" data-testid="new-list-name-input" /></div>
            <div className="space-y-1.5">
              <Label>Color</Label>
              <div className="flex gap-2">
                {SWATCHES.map((c) => (
                  <button key={c} type="button" onClick={() => setColor(c)} className={`h-8 w-8 rounded-full border-2 ${color === c ? 'border-foreground' : 'border-transparent'}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <Button onClick={createNew} disabled={saving} className="w-full h-11 gap-2" data-testid="create-list-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save list
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Dialog to pick contacts to add into a specific list (used on Lists page)
export const ContactPickerDialog = ({ open, onOpenChange, excludeIds = [], onPick }) => {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      setLoading(true);
      const params = search.trim() ? { search: search.trim() } : {};
      api.get('/contacts', { params }).then((r) => setContacts(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [open, search]);

  const available = contacts.filter((c) => !excludeIds.includes(c.id));
  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const close = (v) => { if (!v) { setSelected(new Set()); setSearch(''); } onOpenChange(v); };

  const confirm = async () => {
    if (selected.size === 0) { toast.error('Select at least one contact'); return; }
    setSaving(true);
    await onPick([...selected]);
    setSaving(false);
    close(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="contact-picker-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add contacts to list</DialogTitle></DialogHeader>
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts" className="h-11 pl-9" data-testid="picker-search" />
        </div>
        <div className="max-h-72 overflow-auto mt-3 rounded-xl border border-border">
          {loading ? <p className="p-6 text-sm text-muted-foreground text-center">Loading…</p> :
            available.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">No contacts available.</p> :
              available.map((c) => (
                <label key={c.id} className={`flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0 cursor-pointer ${selected.has(c.id) ? 'bg-primary/5' : 'hover:bg-secondary/40'}`}>
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                  <div className="min-w-0"><div className="text-sm font-medium">{`${c.first_name} ${c.last_name}`.trim() || c.email}</div><div className="text-xs text-muted-foreground">{c.email}</div></div>
                </label>
              ))}
        </div>
        <DialogFooter>
          <Button onClick={confirm} disabled={saving || selected.size === 0} className="w-full h-11 gap-2" data-testid="picker-confirm">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Add {selected.size || ''} contacts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
